







































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { RootState } from '../../../../store/store';
import { ApiState } from '@/store/types/general.types';
import {
  AddressCountry,
  AddressState,
  AddressFilterOption
} from '../../../../store/modules/admin/types/group-level-attribute.types';
import DraggableIcon from './DraggableIcon.vue';
import { ToastProgrammatic as Toast } from 'buefy';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { clone } from '@/jbi-shared/util/group-level-attributes.util';
import {
  GroupLevelAttributeOption,
  GroupLevelAttributeValue
} from '@/jbi-shared/types/jaas-group-level-attributes.types';
import AttributeActionButtons from './AttributeActionButtons.vue';
import { getUniqueByProperty } from '@/jbi-shared/util/string.utils';
import {
  isEmail,
  isContact as isPhone
} from '@/jbi-shared/util/validate-email-domains.util';
import { VerifiedUsers } from '@/jbi-shared/types/user.types';
import UserLookupAutoComplete from './components/UserLookupAutoComplete.vue';

@Component({
  components: {
    DraggableIcon,
    AttributeActionButtons,
    UserLookupAutoComplete
  }
})
export default class GroupLevelAttributeAdministratorFeeField extends Vue {
  @Prop() attributeValue!: GroupLevelAttributeValue;
  @Prop() fieldClasses!: any;
  @Prop() duplicationError!: boolean;
  @Prop({ default: false }) isSettings!: boolean;
  @Prop() deleteAttribute!: () => void;
  @Prop() updateAttributeValue!: (attribute: GroupLevelAttributeValue) => void;
  @Prop() toggleAttributeRequire!: (
    attribute: GroupLevelAttributeValue
  ) => void;
  @Prop() editAttribute!: (attribute: GroupLevelAttributeValue) => void;

  nameErrorMsg: string = '';
  emailErrorMsg: string = '';
  phoneErrorMsg: string = '';
  amountErrorMsg: string = '';
  currencyErrorMsg: string = '';

  currentCountry: AddressCountry | undefined = undefined;
  currentStates: AddressState[] = [];
  selectedCountryId: number = 0; // used to determine whether to store fetched states
  isPhoneFormatCorrect: boolean = false;
  isEmailFormatCorrect: boolean = false;
  isActive: boolean = false;

  // Cloned array to prevent mutations to state
  clonedCountries: AddressCountry[] = [];

  selectedCurrency: string = '';
  inputAmount: string = '';
  inputMarkAsPaid: boolean = false;
  clonedAttributeValueObject: GroupLevelAttributeValue | null = null;
  tooltipDescription: string = `After you publish, we’ll send the order contact an email containing a secure payment link for the administration fee.`;

  // Flag for triggering custom error message for name field upon no user found
  noUserFoundError: boolean = false;
  userFromInput: string = '';

  // Shorthand referenced for rendering only
  get currency() {
    return this.attributeValueAsGLAOptionArray[0];
  }
  get amount() {
    return this.attributeValueAsGLAOptionArray[1];
  }
  get name() {
    return this.attributeValueAsGLAOptionArray[2];
  }
  get email() {
    return this.attributeValueAsGLAOptionArray[3];
  }
  get phone() {
    return this.attributeValueAsGLAOptionArray[4];
  }

  inputName: string = '';
  inputEmail: string = '';
  inputPhone: string = '';

  @Action('admin/getCountries')
  getAllCountries!: (addressFilter?: AddressFilterOption) => void;

  @State(({ admin }: RootState) => admin.apiState.getCountries)
  getAllCountriesApiState!: ApiState;

  @State(({ admin }: RootState) => admin.countries)
  allCountries!: AddressCountry[];

  mounted() {
    this.getAllCountries();
    this.prepopulateFields();
  }

  get attributeValueAsGLAOptionArray(): GroupLevelAttributeOption[] {
    if (!this.attributeValue.value) {
      return this.attributeValue.groupLevelAttributeType
        .option as GroupLevelAttributeOption[];
    }
    return this.attributeValue.value as GroupLevelAttributeOption[];
  }

  // Computed property so changes from the value from parent component are synced
  get isRequired() {
    return this.attributeValue.groupLevelAttributeSpec.isRequired;
  }

  get currencyList(): AddressCountry[] {
    if (this.clonedCountries) {
      return getUniqueByProperty(this.clonedCountries, 'currency').filter(
        (country) => {
          return country.currency
            .toLowerCase()
            .includes((this.selectedCurrency as string).toLowerCase());
        }
      );
    }

    return [];
  }

  toggleIsActive() {
    this.isActive = !this.isActive;
  }

  /**
   * DO NOT convert this method into a getter.
   */
  fieldErrorMessages(): string[] {
    return [
      this.amountErrorMsg,
      this.currencyErrorMsg,
      this.nameErrorMsg,
      this.emailErrorMsg,
      this.phoneErrorMsg
    ];
  }

  verifyName() {
    if (this.isRequired && !this.inputName) {
      this.nameErrorMsg = `${this.name.placeholder} is required.`;
      this.attributeValue.hasFieldError = true;
      this.updateAttributeValue(this.attributeValue);
      return;
    }

    this.nameErrorMsg = '';
    this.attributeValue.hasFieldError = false;
    this.updateAttributeValue(this.attributeValue);
  }

  validateEmailFormat(): void {
    let hasError = false;
    let errorMessage = '';

    if (this.isRequired && !this.inputEmail) {
      // 1. Check if required and empty
      this.emailErrorMsg = `${this.email.placeholder} is required.`;
      hasError = true;
    }

    if (this.inputEmail && !isEmail(this.inputEmail)) {
      // 2. Check if it has a value but the format is invalid
      errorMessage = 'Invalid email format.';
      hasError = true;
    }

    this.emailErrorMsg = errorMessage;
    this.attributeValue.hasFieldError = hasError;

    this.updateAttributeValue(this.attributeValue);
  }

  /**
   * Check for currency name when user types manually.
   * Assign corrected value (string case) if input matched
   */
  verifyCurrencyInput(): void {
    if (
      this.isSettings &&
      (!this.clonedCountries || this.clonedCountries.length === 0)
    ) {
      return;
    }

    this.currencyErrorMsg = '';

    // Validate against empty/null values
    if (!this.selectedCurrency && this.isRequired) {
      this.currencyErrorMsg = `${this.currency.placeholder} is required.`;
      return;
    }

    const targetCountry: AddressCountry | undefined = this.clonedCountries.find(
      (country) =>
        country.currency.toLowerCase() ===
        (this.selectedCurrency as string).toLowerCase()
    );

    if (!targetCountry) {
      this.currencyErrorMsg = 'Invalid currency.';
      return;
    }

    if (targetCountry) {
      this.selectedCurrency = targetCountry.currency;
    }

    this.currencyErrorMsg = '';
    return;
  }

  verifyAmount(input?: string): void {
    input = input || (this.inputAmount as string);

    this.amountErrorMsg = '';

    // Required field validation
    if ((this.isRequired && !input) || input === '0' || input === '0.00') {
      this.amountErrorMsg = `${this.amount.placeholder} is required.`;
      return;
    }

    if (input) {
      // Remove any non-numeric characters except for a decimal point
      const sanitizedInput = input.replace(/[^\d.]/g, '');

      // Check if the input has a valid currency format (X.XX)
      const currencyRegex = /^\d*\.?\d{0,2}$/;
      if (!currencyRegex.test(sanitizedInput)) {
        this.amountErrorMsg =
          'Please enter a valid currency amount, e.g. 10.00';
        return;
      }

      // Ensure value provided is a non-negative number
      const numericValue = parseFloat(sanitizedInput);
      if (isNaN(numericValue)) {
        this.amountErrorMsg = 'Please enter a valid number';
      } else if (numericValue < 0) {
        this.amountErrorMsg = 'Amount cannot be negative';
      }

      this.inputAmount = sanitizedInput;
    }
  }

  formatAmount(): void {
    if (this.inputAmount) {
      // Parse the current value to a number
      const numericValue = parseFloat(this.inputAmount as string);

      // If it's a valid number, format it to 2 decimal places
      if (!isNaN(numericValue)) {
        // Format to 2 decimal places
        this.inputAmount = numericValue.toFixed(2);
      } else {
        // If not a valid number, reset to empty or default value
        this.inputAmount = '';
      }
    }
  }

  updateErrorState(): boolean {
    this.attributeValue.hasFieldError =
      this.fieldErrorMessages().join('').length > 0;
    return this.attributeValue.hasFieldError;
  }

  /**
   * This method is triggered when user selects an item from the b-autocomplete dropdown.
   * Although there is v-model binding to the `selectedCurrency` variable, b-autocomplete
   * emits selected items as separate event via `@selected`, so a separate method is required
   * to handle the update.
   */
  assignSelectedCurrency(selectedCountry: AddressCountry | null): void {
    this.selectedCurrency = selectedCountry ? selectedCountry.currency : '';
  }

  handleNoUserFound(userInput: string) {
    this.inputName = userInput;
    this.verifyName();
  }

  handleSelectUser(selectedItem: VerifiedUsers) {
    // this.noUserFoundError = false;
    if (!selectedItem) {
      // Clear fields
      this.inputName = '';
      this.inputEmail = '';
      this.inputPhone = '';
      return;
    }
    this.inputName = `${selectedItem.firstName} ${selectedItem.lastName}`;
    this.inputEmail = selectedItem.userEmail;
    this.inputPhone = selectedItem.phoneNumber;
  }

  @Watch('isRequired')
  validateRequiredFieldValues(): void {
    this.verifyCurrencyInput();
    this.verifyAmount();
    this.verifyName();
    this.validateEmailFormat();
    this.validatePhoneFormat();
  }

  @isTruthy
  @Watch('getAllCountriesApiState.error')
  @Watch('getCountryByIdApiState.error')
  addressApiErrorCallback(): void {
    Toast.open('Error fetching countries and states.');
  }

  @Watch('getAllCountriesApiState.success')
  validateCountryCallback(): void {
    this.validateAndUpdate();
    this.clonedCountries = clone(this.allCountries);
  }

  @Watch('selectedCurrency')
  @Watch('inputAmount')
  @Watch('inputName')
  @Watch('inputEmail')
  validateAndUpdate(): void {
    this.validateRequiredFieldValues();
    const clonedCurrency = clone(this.currency);
    const clonedAmount = clone(this.amount);
    const clonedName = clone(this.name);
    const clonedEmail = clone(this.email);

    if (this.clonedAttributeValueObject) {
      (this.clonedAttributeValueObject
        .value as GroupLevelAttributeOption[])[0] = {
        ...clonedCurrency,
        value: this.selectedCurrency
      };
      (this.clonedAttributeValueObject
        .value as GroupLevelAttributeOption[])[1] = {
        ...clonedAmount,
        value: this.inputAmount
      };
      (this.clonedAttributeValueObject
        .value as GroupLevelAttributeOption[])[2] = {
        ...clonedName,
        value: this.inputName.toString()
      };
      (this.clonedAttributeValueObject
        .value as GroupLevelAttributeOption[])[3] = {
        ...clonedEmail,
        value: this.inputEmail.toString()
      };

      this.updateAttributeValue(this.clonedAttributeValueObject);
    }
  }

  @Watch('inputPhone')
  validatePhoneFormat(): void {
    if (this.isRequired && !this.inputPhone) {
      this.phoneErrorMsg = `${this.phone.placeholder} is required.`;
      this.attributeValue.hasFieldError = true;
      this.updateAttributeValue(this.attributeValue);
      return;
    } else if (this.inputPhone) {
      // run validator when there's value
      this.isPhoneFormatCorrect = isPhone(this.inputPhone);
    }

    if (!this.isPhoneFormatCorrect || !this.inputPhone) {
      this.phoneErrorMsg = 'Invalid phone format.';
      this.attributeValue.hasFieldError = true;
    } else {
      this.phoneErrorMsg = '';
      this.attributeValue.hasFieldError = false;
      const clonedPhone = clone(this.phone);
      if (this.clonedAttributeValueObject) {
        (this.clonedAttributeValueObject
          .value as GroupLevelAttributeOption[])[4] = {
          ...clonedPhone,
          value: this.inputPhone.toString()
        };
        this.updateAttributeValue(this.clonedAttributeValueObject);
        return;
      }
    }

    this.updateAttributeValue(this.attributeValue);
  }

  @Watch('phoneErrorMsg')
  @Watch('currencyErrorMsg')
  @Watch('amountErrorMsg')
  checkErrorMessageCallback(): void {
    this.updateErrorState();
    this.updateAttributeValue(this.attributeValue);
  }

  /**
   * During course creation, a new/existing attribute's `attributeValue.value` is null.
   * If it's course edit, `attributeValue.value` is NOT NULL, and will be pre-populated with values.
   *
   * In order to emit the user inputs/updates to the parent component, a clonedAttributeValueObject
   * is utilized to prevent directly mutating state. Depending on whether `attribute.value` is null,
   * the cloning logic will vary.
   */
  prepopulateFields(): void {
    if (!this.attributeValue.value) {
      // Initialise `attributeValue.value` with default object before cloning
      this.attributeValue.value = this.attributeValue.groupLevelAttributeType
        .option as GroupLevelAttributeOption[];
      this.clonedAttributeValueObject = { ...this.attributeValue };
    } else {
      this.clonedAttributeValueObject = { ...this.attributeValue };
      // Assign existing value to local variable
      this.selectedCurrency = (this.attributeValue
        .value as GroupLevelAttributeOption[])[0].value as string;
      this.inputAmount = (this.attributeValue
        .value as GroupLevelAttributeOption[])[1].value as string;
      this.inputName = (this.attributeValue
        .value as GroupLevelAttributeOption[])[2].value as string;
      this.inputEmail = (this.attributeValue
        .value as GroupLevelAttributeOption[])[3].value as string;
      this.inputPhone = (this.attributeValue
        .value as GroupLevelAttributeOption[])[4].value as string;
    }
  }
}
